import type {
  OperatingHours,
  ReceivingHours,
  SpecialHours,
  TimeRange,
  Weekday,
} from '@/apis/types';

export const WEEKDAYS: { key: Weekday; label: string; short: string }[] = [
  { key: 'mon', label: 'Monday', short: 'Mon' },
  { key: 'tue', label: 'Tuesday', short: 'Tue' },
  { key: 'wed', label: 'Wednesday', short: 'Wed' },
  { key: 'thu', label: 'Thursday', short: 'Thu' },
  { key: 'fri', label: 'Friday', short: 'Fri' },
  { key: 'sat', label: 'Saturday', short: 'Sat' },
  { key: 'sun', label: 'Sunday', short: 'Sun' },
];

// Match the backend limits in onboarding/operating_hours.py.
export const MAX_RANGES_PER_DAY = 6;
export const MAX_SPECIAL_HOURS = 100;
/** Longest special-hours span: end_date - start_date is at most 365 days. */
export const MAX_SPECIAL_SPAN_DAYS = 366;
export const DEFAULT_RANGE: TimeRange = { start: '08:00', end: '18:00' };
const END_OF_DAY = 24 * 60;
const TIME_STEP_MINUTES = 15;

export const toMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

export const fromMinutes = (total: number): string => {
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const buildTimeOptions = (from: number, to: number) => {
  const options: { value: string; label: string }[] = [];
  for (let m = from; m <= to; m += TIME_STEP_MINUTES) {
    options.push({ value: fromMinutes(m), label: fromMinutes(m) });
  }
  return options;
};

export const START_TIME_OPTIONS = buildTimeOptions(
  0,
  END_OF_DAY - TIME_STEP_MINUTES
);
export const END_TIME_OPTIONS = buildTimeOptions(TIME_STEP_MINUTES, END_OF_DAY);

const emptyWeekly = (): Record<Weekday, TimeRange[]> => ({
  mon: [],
  tue: [],
  wed: [],
  thu: [],
  fri: [],
  sat: [],
  sun: [],
});

/** The "Weekdays 8 to 6" preset: Mon–Fri 08:00–18:00, weekend closed. */
export const weekdays8To6 = (): Record<Weekday, TimeRange[]> => {
  const weekly = emptyWeekly();
  (['mon', 'tue', 'wed', 'thu', 'fri'] as Weekday[]).forEach((day) => {
    weekly[day] = [{ ...DEFAULT_RANGE }];
  });
  return weekly;
};

const sameRanges = (a: TimeRange[], b: TimeRange[]) =>
  a.length === b.length &&
  a.every((r, i) => r.start === b[i].start && r.end === b[i].end);

export const isWeekdays8To6 = (weekly: Record<Weekday, TimeRange[]>) => {
  const preset = weekdays8To6();
  return WEEKDAYS.every(({ key }) => sameRanges(weekly[key], preset[key]));
};

const isTime = (value: unknown, allowEndOfDay: boolean): value is string =>
  typeof value === 'string' &&
  (/^([01]\d|2[0-3]):[0-5]\d$/.test(value) ||
    (allowEndOfDay && value === '24:00'));

const parseRanges = (value: unknown): TimeRange[] =>
  Array.isArray(value)
    ? value
        .filter(
          (r): r is TimeRange =>
            !!r && isTime(r.start, false) && isTime(r.end, true)
        )
        .map((r) => ({ start: r.start, end: r.end }))
    : [];

/** Defensively reads a stored value; null when nothing usable is stored. */
export const parseOperatingHours = (raw: unknown): OperatingHours | null => {
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const weeklyRaw = (record.weekly ?? {}) as Record<string, unknown>;
  const weekly = emptyWeekly();
  WEEKDAYS.forEach(({ key }) => {
    weekly[key] = parseRanges(weeklyRaw[key]);
  });
  // The panel edits and deletes entries by id, so keep ids unique.
  const seenIds = new Set<string>();
  const uniqueId = (id: string | undefined, index: number) => {
    const value = id && !seenIds.has(id) ? id : `special-${index}`;
    seenIds.add(value);
    return value;
  };
  const special = Array.isArray(record.special_hours)
    ? (record.special_hours as Partial<SpecialHours>[])
        .filter((s) => s && typeof s.name === 'string' && !!s.start_date)
        .map((s, index) => ({
          id: uniqueId(s.id, index),
          name: s.name as string,
          start_date: s.start_date as string,
          end_date: s.end_date || null,
          closed: s.closed !== false,
          hours: parseRanges(s.hours),
          repeats_yearly: !!s.repeats_yearly,
        }))
    : [];
  return {
    timezone: typeof record.timezone === 'string' ? record.timezone : null,
    weekly,
    special_hours: special,
  };
};

/**
 * The old single receiving-hours range as a schedule. It has no days, so it
 * applies to every day. Its timestamps are read in the location's time zone
 * (the browser's when unknown); a range past midnight becomes two ranges and
 * equal times mean open all day. Null when the range is incomplete.
 */
/** Decodes JSON that arrived as text, possibly encoded more than once. */
const decodeJsonText = (value: unknown): unknown => {
  let decoded = value;
  for (let i = 0; i < 5 && typeof decoded === 'string' && decoded; i += 1) {
    try {
      decoded = JSON.parse(decoded);
    } catch {
      break;
    }
  }
  return decoded;
};

export const hoursFromLegacy = (
  raw?: ReceivingHours | string | null,
  timeZone?: string | null
): OperatingHours | null => {
  // Importers can write this back as JSON text (RDS Data API round trip).
  const decoded = decodeJsonText(raw);
  const legacy =
    decoded && typeof decoded === 'object'
      ? (decoded as Partial<ReceivingHours>)
      : null;
  if (!legacy?.start || !legacy?.end) return null;
  const startDate = new Date(legacy.start);
  const endDate = new Date(legacy.end);
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return null;
  const localMinutes = (date: Date) =>
    timeZone
      ? zonedNow(timeZone, date).minutes
      : date.getHours() * 60 + date.getMinutes();
  const start = localMinutes(startDate);
  const end = localMinutes(endDate);
  let ranges: TimeRange[];
  if (start === end) {
    ranges = [{ start: '00:00', end: '24:00' }];
  } else if (start < end) {
    ranges = [{ start: fromMinutes(start), end: fromMinutes(end) }];
  } else {
    ranges = [
      ...(end > 0 ? [{ start: '00:00', end: fromMinutes(end) }] : []),
      { start: fromMinutes(start), end: '24:00' },
    ];
  }
  const weekly = emptyWeekly();
  WEEKDAYS.forEach(({ key }) => {
    weekly[key] = ranges.map((r) => ({ ...r }));
  });
  return { timezone: null, weekly, special_hours: [] };
};

/**
 * What a location's hours are: the saved schedule, else the legacy range
 * (still written by importers), else null. The card, table and editor all
 * read hours through this so they agree.
 */
export const effectiveOperatingHours = (
  stored: unknown,
  legacy?: ReceivingHours | string | null,
  timeZone?: string | null
): OperatingHours | null =>
  parseOperatingHours(stored) ?? hoursFromLegacy(legacy, timeZone);

/** Starting point when nothing is set: the "Weekdays 8 to 6" preset. */
export const createOperatingHours = (
  timezone: string | null
): OperatingHours => ({
  timezone,
  weekly: weekdays8To6(),
  special_hours: [],
});

/** Next range to offer from "Add hours": starts where the latest one ends. */
export const nextRange = (ranges: TimeRange[]): TimeRange | null => {
  if (ranges.length === 0) return { ...DEFAULT_RANGE };
  const lastEnd = Math.max(...ranges.map((r) => toMinutes(r.end)));
  if (lastEnd >= END_OF_DAY) return null;
  return {
    start: fromMinutes(lastEnd),
    end: fromMinutes(Math.min(lastEnd + 120, END_OF_DAY)),
  };
};

/** Error for a day's ranges, or null when they are valid. */
export const rangesError = (ranges: TimeRange[]): string | null => {
  if (ranges.some((r) => toMinutes(r.start) >= toMinutes(r.end))) {
    return 'End time must be after start time';
  }
  const sorted = [...ranges].sort(
    (a, b) => toMinutes(a.start) - toMinutes(b.start)
  );
  for (let i = 1; i < sorted.length; i += 1) {
    if (toMinutes(sorted[i].start) < toMinutes(sorted[i - 1].end)) {
      return 'Hours overlap';
    }
  }
  return null;
};

export const sortRanges = (ranges: TimeRange[]) =>
  [...ranges].sort((a, b) => toMinutes(a.start) - toMinutes(b.start));

/** "08:00" -> "8:00 AM"; "24:00" -> "12:00 AM". */
export const formatTime = (time: string): string => {
  const total = toMinutes(time) % END_OF_DAY;
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  const suffix = hours < 12 ? 'AM' : 'PM';
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour12}:${String(minutes).padStart(2, '0')} ${suffix}`;
};

/**
 * For display only: back-to-back (or overlapping) ranges as one, e.g.
 * 08:00-18:00 + 18:00-20:00 -> 08:00-20:00. Saved data keeps the split.
 */
export const mergeRanges = (ranges: TimeRange[]): TimeRange[] =>
  sortRanges(ranges).reduce<TimeRange[]>((merged, range) => {
    const last = merged[merged.length - 1];
    if (last && toMinutes(range.start) <= toMinutes(last.end)) {
      if (toMinutes(range.end) > toMinutes(last.end)) last.end = range.end;
    } else {
      merged.push({ ...range });
    }
    return merged;
  }, []);

export const formatRanges = (rawRanges: TimeRange[]): string => {
  const ranges = mergeRanges(rawRanges);
  if (ranges.length === 0) return 'Closed';
  if (ranges.length === 1 && ranges[0].start === '00:00') {
    if (ranges[0].end === '24:00') return 'Open 24 hours';
  }
  return ranges
    .map((r) => `${formatTime(r.start)} to ${formatTime(r.end)}`)
    .join(', ');
};

/**
 * Consecutive days with the same hours, e.g. "Tue to Thu". Compares the
 * joined ranges, so a split 8-12 + 12-6 day groups with a plain 8-6 day.
 */
export const groupWeekly = (
  weekly: Record<Weekday, TimeRange[]>
): { label: string; ranges: TimeRange[] }[] => {
  const groups: { first: number; last: number; ranges: TimeRange[] }[] = [];
  WEEKDAYS.forEach(({ key }, index) => {
    const ranges = mergeRanges(weekly[key]);
    const previous = groups[groups.length - 1];
    if (previous && sameRanges(previous.ranges, ranges)) {
      previous.last = index;
    } else {
      groups.push({ first: index, last: index, ranges });
    }
  });
  return groups.map(({ first, last, ranges }) => ({
    label:
      first === 0 && last === WEEKDAYS.length - 1
        ? 'Every day'
        : first === last
          ? WEEKDAYS[first].short
          : `${WEEKDAYS[first].short} to ${WEEKDAYS[last].short}`,
    ranges,
  }));
};

export const weeklyTotals = (weekly: Record<Weekday, TimeRange[]>) => {
  let openDays = 0;
  let minutes = 0;
  WEEKDAYS.forEach(({ key }) => {
    if (weekly[key].length > 0) openDays += 1;
    weekly[key].forEach((r) => {
      minutes += toMinutes(r.end) - toMinutes(r.start);
    });
  });
  return { openDays, hoursPerWeek: Math.round((minutes / 60) * 10) / 10 };
};

/** "Open 6 days · 53 hrs/week" */
export const formatWeeklyTotals = (
  weekly: Record<Weekday, TimeRange[]>
): string => {
  const { openDays, hoursPerWeek } = weeklyTotals(weekly);
  if (openDays === 0) return 'Closed all week';
  const days =
    openDays === 7
      ? 'Open every day'
      : `Open ${openDays} ${openDays === 1 ? 'day' : 'days'}`;
  return `${days} · ${hoursPerWeek} ${hoursPerWeek === 1 ? 'hr' : 'hrs'}/week`;
};

/** "America/Denver" -> "Mountain Time"; falls back to the IANA name. */
export const timeZoneLabel = (timeZone: string, at = new Date()): string => {
  try {
    const part = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'longGeneric',
    })
      .formatToParts(at)
      .find((p) => p.type === 'timeZoneName');
    return part?.value || timeZone;
  } catch {
    return timeZone;
  }
};

const FALLBACK_TIME_ZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Asia/Kolkata',
  'Asia/Tokyo',
  'Australia/Sydney',
];

export const timeZoneOptions = (current?: string | null): string[] => {
  let zones: string[] = FALLBACK_TIME_ZONES;
  const intl = Intl as typeof Intl & {
    supportedValuesOf?: (key: string) => string[];
  };
  if (typeof intl.supportedValuesOf === 'function') {
    zones = intl.supportedValuesOf('timeZone');
  }
  if (!zones.includes('UTC')) zones = ['UTC', ...zones];
  return current && !zones.includes(current) ? [current, ...zones] : zones;
};

// ---- Dates (YYYY-MM-DD strings, calendar math in UTC) ----

const parseDate = (value: string) => {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

const toDateString = (date: Date) => date.toISOString().slice(0, 10);

const addDays = (value: string, days: number) => {
  const date = parseDate(value);
  date.setUTCDate(date.getUTCDate() + days);
  return toDateString(date);
};

/** Whole days from `start` to `end` (YYYY-MM-DD). */
export const daysBetween = (start: string, end: string): number =>
  Math.round(
    (parseDate(end).getTime() - parseDate(start).getTime()) / 86_400_000
  );

const weekdayOf = (value: string): Weekday =>
  WEEKDAYS[(parseDate(value).getUTCDay() + 6) % 7].key;

/** "Thu, Nov 26, 2026" (or "Nov 26" without weekday/year). */
export const formatDate = (value: string, long = true): string =>
  parseDate(value).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    ...(long
      ? { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }
      : { month: 'short', day: 'numeric' }),
  });

/** "Thu, Nov 26, 2026 · Closed · Yearly" */
export const describeSpecialHours = (entry: SpecialHours): string => {
  const dates = entry.end_date
    ? `${formatDate(entry.start_date)} to ${formatDate(entry.end_date)}`
    : formatDate(entry.start_date);
  const hours = entry.closed ? 'Closed' : formatRanges(entry.hours);
  return [dates, hours, entry.repeats_yearly ? 'Yearly' : null]
    .filter(Boolean)
    .join(' · ');
};

const isLeapYear = (year: number) =>
  (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

/** `date`'s month and day in `year`; Feb 29 falls on Feb 28 in other years. */
const yearlyDate = (date: string, year: number) => {
  const monthDay = date.slice(5);
  return monthDay === '02-29' && !isLeapYear(year)
    ? `${year}-02-28`
    : `${year}-${monthDay}`;
};

/**
 * The dates an entry covers in the year of `today` or later, as
 * [start, end]; null when a one-off entry is already over.
 */
export const nextOccurrence = (
  entry: SpecialHours,
  today: string
): [string, string] | null => {
  const end = entry.end_date || entry.start_date;
  if (!entry.repeats_yearly) {
    return end >= today ? [entry.start_date, end] : null;
  }
  const firstYear = Number(entry.start_date.slice(0, 4));
  // Ranges past New Year end in a later year; keep that offset.
  const endYearOffset = Number(end.slice(0, 4)) - firstYear;
  const year = Number(today.slice(0, 4));
  // From last year (an entry may still be running) up to the first year
  // that can reach `today`; never before the entry's own first year.
  const fromYear = Math.max(firstYear, year - 1);
  for (let y = fromYear; y <= Math.max(fromYear, year + 1); y += 1) {
    // Shift both calendar dates, not a day count, so Feb 28 - Mar 1 stays
    // that range in leap years.
    const start = yearlyDate(entry.start_date, y);
    const shiftedEnd = yearlyDate(end, y + endYearOffset);
    if (shiftedEnd >= today) return [start, shiftedEnd];
  }
  return null;
};

/**
 * The entry that sets `date`'s hours when several cover it: a one-off date
 * beats a yearly one, then the shorter range, then the later entry. So a
 * one-off opening on a day of a yearly closure applies. Matches the backend
 * contract in onboarding/operating_hours.py.
 */
const specialFor = (
  entries: SpecialHours[],
  date: string
): SpecialHours | undefined => {
  let best: SpecialHours | undefined;
  let bestRank: [number, number, number] | undefined;
  for (const [index, entry] of entries.entries()) {
    const occurrence = nextOccurrence(entry, date);
    if (!occurrence || occurrence[0] > date || date > occurrence[1]) continue;
    const rank: [number, number, number] = [
      entry.repeats_yearly ? 1 : 0,
      daysBetween(occurrence[0], occurrence[1]),
      -index,
    ];
    const better =
      !bestRank ||
      rank[0] < bestRank[0] ||
      (rank[0] === bestRank[0] &&
        (rank[1] < bestRank[1] ||
          (rank[1] === bestRank[1] && rank[2] < bestRank[2])));
    if (better) {
      best = entry;
      bestRank = rank;
    }
  }
  return best;
};

/** Upcoming (or ongoing) entries, soonest first. */
export const upcomingSpecialHours = (entries: SpecialHours[], today: string) =>
  entries
    .map((entry) => ({ entry, occurrence: nextOccurrence(entry, today) }))
    .filter(
      (item): item is { entry: SpecialHours; occurrence: [string, string] } =>
        item.occurrence !== null
    )
    .sort((a, b) => a.occurrence[0].localeCompare(b.occurrence[0]));

const rangesOn = (hours: OperatingHours, date: string): TimeRange[] => {
  const special = specialFor(hours.special_hours, date);
  if (special) return special.closed ? [] : sortRanges(special.hours);
  return sortRanges(hours.weekly[weekdayOf(date)]);
};

/** Wall-clock date and minute-of-day in `timeZone`. */
export const zonedNow = (
  timeZone: string | null,
  now: Date
): { date: string; minutes: number } => {
  const tz = timeZone || 'UTC';
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(now);
  } catch {
    return zonedNow('UTC', now);
  }
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    minutes: Number(get('hour')) * 60 + Number(get('minute')),
  };
};

/** "" for today, "tomorrow " or a short weekday like "Mon " otherwise. */
const dayPrefix = (date: string, offset: number) => {
  if (offset === 0) return '';
  if (offset === 1) return 'tomorrow ';
  const day = WEEKDAYS.find((w) => w.key === weekdayOf(addDays(date, offset)));
  return `${day?.short} `;
};

/** "Open now · closes 6:00 PM" / "Closed now · opens Mon 8:00 AM". */
export const openStatus = (
  hours: OperatingHours,
  now: Date
): { open: boolean; label: string } => {
  const { date, minutes } = zonedNow(hours.timezone, now);
  const today = rangesOn(hours, date);
  const current = today.find(
    (r) => toMinutes(r.start) <= minutes && minutes < toMinutes(r.end)
  );
  if (current) {
    // Follow back-to-back ranges (12:00 then 12:00, or 24:00 then the next
    // day's 00:00) to the real closing time.
    let closeDay = 0;
    let closeAt = toMinutes(current.end);
    for (let step = 0; step < 8 * MAX_RANGES_PER_DAY; step += 1) {
      if (closeAt === END_OF_DAY) {
        if (closeDay >= 7) return { open: true, label: 'Open 24 hours' };
        const next = rangesOn(hours, addDays(date, closeDay + 1))[0];
        if (!next || toMinutes(next.start) !== 0) break;
        closeDay += 1;
        closeAt = toMinutes(next.end);
      } else {
        const dayRanges = rangesOn(hours, addDays(date, closeDay));
        const touching = dayRanges.find((r) => toMinutes(r.start) === closeAt);
        if (!touching) break;
        closeAt = toMinutes(touching.end);
      }
    }
    // "Midnight" names the end of that day; "12:00 AM" would read as its
    // start.
    const closing =
      closeAt === END_OF_DAY ? 'at midnight' : formatTime(fromMinutes(closeAt));
    return {
      open: true,
      label: `Open now · closes ${dayPrefix(date, closeDay)}${closing}`,
    };
  }
  const laterToday = today.find((r) => toMinutes(r.start) > minutes);
  if (laterToday) {
    return {
      open: false,
      label: `Closed now · opens ${formatTime(laterToday.start)}`,
    };
  }
  for (let offset = 1; offset <= 7; offset += 1) {
    const day = addDays(date, offset);
    const first = rangesOn(hours, day)[0];
    if (first) {
      return {
        open: false,
        label: `Closed now · opens ${dayPrefix(date, offset)}${formatTime(
          first.start
        )}`,
      };
    }
  }
  return { open: false, label: 'Closed now' };
};

/** Today's date in `timeZone`, or in the browser's zone when unknown. */
export const todayIn = (timeZone: string | null, now = new Date()) => {
  if (timeZone) return zonedNow(timeZone, now).date;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const DAY_KEYS = new Map(WEEKDAYS.map((d) => [d.key as string, d.label]));

/**
 * Server validation text in the editor's words: "weekly.mon[0] must end
 * after it starts." -> "Monday hours must end after it starts." and
 * "special_hours[1]..." -> the entry's name.
 */
export const describeServerError = (
  message: string,
  hours?: OperatingHours | null
): string =>
  message
    .replace(/^operating_hours:\s*/, '')
    .replace(/weekly\.(\w+)(\[\d+\])?(\.\w+)?/g, (match, day: string) => {
      const label = DAY_KEYS.get(day);
      return label ? `${label} hours` : match;
    })
    .replace(
      /special_hours\[(\d+)\](?:\.(\w+))?/g,
      (_match, index: string, field?: string) => {
        const name = hours?.special_hours[Number(index)]?.name;
        const subject = name ? `"${name}"` : 'A holiday or special date';
        const fields: Record<string, string> = {
          name: 'name',
          start_date: 'date',
          end_date: 'end date',
          hours: 'hours',
        };
        return field && fields[field] ? `${subject} ${fields[field]}` : subject;
      }
    );
